/**
 * Current Cycles pane – lists company cycles with employee count, delete, deactivate
 */
import React, { useState, useEffect, useCallback } from "react";
import { translateUi } from '../i18n/index';
import { Trash2, NoSymbol, RotateCcw, ExclamationTriangle, Edit2, Eye, Lock } from "../icons";
import { ConfirmActionModal } from "../ConfirmActionModal";
import { cb, cbCn } from "./cycleBuilderTheme";
import { formatCycleDurationLabel, resolveDisplayedCycleMonths } from "./formatCycleDuration";
import { TemplateCriteriaPreviewModal } from "./TemplateCriteriaPreviewModal";
import { AddAssessmentEventsModal } from "./AddAssessmentEventsModal";
import { AssessmentEventsPreviewModal, type CycleEventPreviewRow } from "./AssessmentEventsPreviewModal";
import { libraryCycleFromApiDetail, type LibraryCycle } from "./cycleLibraryUi";

export interface CycleRow {
  id: number;
  title: string;
  expiry_years: number;
  expiry_months?: number | null;
  company_code: string | null;
  framework_cycle_id?: string | null;
  template_editable_by_company?: number;
  deactivated: number;
  /** 0 = draft (admin only), 1 = managers can assign. */
  released_to_managers?: number;
  employee_count: number;
  /** Latest expiry date (YYYY-MM-DD) across live assignments — when the cycle becomes deletable. */
  assignments_end_on?: string | null;
  /** Continuous assignments renew, so they never reach an end date on their own. */
  continuous_assignment_count?: number;
  /** Configured assessment event templates on this cycle. */
  assessment_event_count?: number;
}

/** Whole days from today until a YYYY-MM-DD date; negative once passed. */
function daysUntil(dateStr: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr.trim());
  if (!m) return null;
  const target = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const now = new Date();
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((target - today) / 86_400_000);
}

function formatEndDate(dateStr: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr.trim());
  if (!m) return dateStr;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
}

/**
 * Plain-English state for one cycle: who is on it, and when a suspended cycle can be deleted.
 * Suspended cycles keep running for people already assigned; nobody new can join.
 */
function describeCycleState(c: CycleRow): { line: string; deletableNow: boolean; unlockHint: string | null } {
  const assigned = Number(c.employee_count ?? 0);
  const continuous = Number(c.continuous_assignment_count ?? 0);
  const suspended = !!c.deactivated;
  const assignedLabel = `${assigned} ${assigned === 1 ? translateUi("employee") : translateUi("employees")} ${translateUi("assigned")}`;

  if (assigned === 0) {
    return {
      line: suspended
        ? `${translateUi("Suspended")} — ${translateUi("nobody assigned, so this can be deleted")}`
        : assignedLabel,
      deletableNow: true,
      unlockHint: null,
    };
  }

  if (continuous > 0) {
    const line = `${assignedLabel} — ${translateUi("includes continuous assignments with no end date")}`;
    return {
      line: suspended ? `${translateUi("Suspended")} — ${line}` : line,
      deletableNow: false,
      unlockHint: translateUi("Continuous assignments have no end date. Close them on the employee record before deleting."),
    };
  }

  const endOn = typeof c.assignments_end_on === "string" ? c.assignments_end_on.trim() : "";
  const days = endOn ? daysUntil(endOn) : null;
  if (!endOn || days == null) {
    const line = suspended ? `${translateUi("Suspended")} — ${assignedLabel}` : assignedLabel;
    return {
      line,
      deletableNow: false,
      unlockHint: translateUi("Employees are still assigned. You can delete this cycle once they finish."),
    };
  }

  if (days <= 0) {
    const line = `${assignedLabel} — ${translateUi("all assignments have run their course")}`;
    return {
      line: suspended ? `${translateUi("Suspended")} — ${line}` : line,
      deletableNow: false,
      unlockHint: translateUi("Assignments have expired but are still open. Close them on the employee record before deleting."),
    };
  }

  const line = translateUi("{{assigned}} — last cycle ends {{date}} ({{days}} days left)", {
    assigned: assignedLabel,
    date: formatEndDate(endOn),
    days,
  });
  return {
    line: suspended ? `${translateUi("Suspended")} — ${line}` : line,
    deletableNow: false,
    unlockHint: translateUi("Employees are still assigned until {{date}} ({{days}} days). You can delete this cycle after that.", {
      date: formatEndDate(endOn),
      days,
    }),
  };
}

export function CurrentCyclesPane({
  companyCode,
  currentUserEmail,
  isSystemAdmin,
  isMobile,
  isDarkMode = false,
  onMounted,
  embedded = false,
  onCountChange,
  onChanged,
  onEditCriteriaInBuilder,
}: {
  companyCode?: string;
  currentUserEmail?: string | null;
  isSystemAdmin?: boolean;
  isMobile?: boolean;
  isDarkMode?: boolean;
  onMounted?: (refresh: () => void) => void;
  /** Skip outer panel chrome when nested in CollapsiblePane. */
  embedded?: boolean;
  onCountChange?: (count: number) => void;
  /** A cycle was renamed, deleted, suspended or reactivated. */
  onChanged?: () => void;
  /** Open the builder to edit criteria on an editable-template draft cycle. */
  onEditCriteriaInBuilder?: (cycle: CycleRow) => void;
}) {
  const [cycles, setCycles] = useState<CycleRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [actioning, setActioning] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [overrideModal, setOverrideModal] = useState<{ id: number; title: string; employee_count: number } | null>(null);
  const [overrideConfirm, setOverrideConfirm] = useState("");
  const [pendingDeactivate, setPendingDeactivate] = useState<CycleRow | null>(null);
  const [renamingId, setRenamingId] = useState<number | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [renameSaving, setRenameSaving] = useState(false);
  const [criteriaPreview, setCriteriaPreview] = useState<LibraryCycle | null>(null);
  const [criteriaPreviewLocked, setCriteriaPreviewLocked] = useState(false);
  const [criteriaPreviewLoading, setCriteriaPreviewLoading] = useState<number | null>(null);
  const [eventsModalCycle, setEventsModalCycle] = useState<CycleRow | null>(null);
  const [eventsPreview, setEventsPreview] = useState<{ cycle: CycleRow; events: CycleEventPreviewRow[] } | null>(null);
  const [eventsPreviewLoading, setEventsPreviewLoading] = useState<number | null>(null);

  const fetchCycles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const url = companyCode
        ? `/api/cycles/company-list?companyCode=${encodeURIComponent(companyCode)}&v=${Date.now()}`
        : `/api/cycles/company-list?v=${Date.now()}`;
      const res = await fetch(url, {
        cache: "no-store",
        headers: currentUserEmail ? { "X-User-Email": currentUserEmail } : {},
      });
      if (res.ok) {
        const data = await res.json();
        const list = Array.isArray(data) ? data : [];
        setCycles(list);
        onCountChange?.(list.length);
      } else {
        const err = await res.json().catch(() => ({}));
        setError(err.error || "Failed to load cycles");
      }
    } catch (err: any) {
      setError(err?.message || "Failed to load cycles");
    } finally {
      setLoading(false);
    }
  }, [companyCode, currentUserEmail, onCountChange]);

  useEffect(() => {
    fetchCycles();
  }, [fetchCycles]);

  useEffect(() => {
    onMounted?.(fetchCycles);
  }, [fetchCycles, onMounted]);

  const handleDelete = async (id: number) => {
    if (!confirm(translateUi("Delete this cycle? This cannot be undone. Only cycles with no assigned employees can be deleted."))) return;
    setActioning(id);
    setError(null);
    try {
      const res = await fetch(`/api/cycles/${id}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json", ...(currentUserEmail ? { "X-User-Email": currentUserEmail } : {}) },
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        await fetchCycles();
        onChanged?.();
      } else {
        setError(data.error || "Failed to delete");
      }
    } catch (err: any) {
      setError(err?.message || "Failed to delete");
    } finally {
      setActioning(null);
    }
  };

  /** Company admins retitle a cycle to their own wording; the global template keeps its own name. */
  const handleRename = async (id: number) => {
    const nextTitle = renameValue.trim();
    if (!nextTitle) {
      setError("Enter a cycle title before saving.");
      return;
    }
    setRenameSaving(true);
    setError(null);
    try {
      const res = await fetch(`/api/cycles/${id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...(currentUserEmail ? { "X-User-Email": currentUserEmail } : {}),
          ...(companyCode ? { "X-Company-Code": companyCode } : {}),
        },
        body: JSON.stringify({ title: nextTitle, companyCode, company_code: companyCode }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error || "Could not rename cycle");
        return;
      }
      setRenamingId(null);
      setRenameValue("");
      await fetchCycles();
      onChanged?.();
    } catch (err: any) {
      setError(err?.message || "Could not rename cycle");
    } finally {
      setRenameSaving(false);
    }
  };

  const handleDeactivate = async (id: number) => {
    setActioning(id);
    setError(null);
    try {
      const res = await fetch(`/api/cycles/${id}/deactivate`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...(currentUserEmail ? { "X-User-Email": currentUserEmail } : {}) },
      });
      if (res.ok) {
        setPendingDeactivate(null);
        await fetchCycles();
        onChanged?.();
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || "Failed to deactivate");
      }
    } catch (err: any) {
      setError(err?.message || "Failed to deactivate");
    } finally {
      setActioning(null);
    }
  };

  const handleReleaseToManagers = async (id: number) => {
    setActioning(id);
    setError(null);
    try {
      const res = await fetch(`/api/cycles/${id}/release-to-managers`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...(currentUserEmail ? { "X-User-Email": currentUserEmail } : {}),
          ...(companyCode ? { "X-Company-Code": companyCode } : {}),
        },
        body: JSON.stringify({ companyCode, company_code: companyCode }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        await fetchCycles();
        onChanged?.();
      } else {
        setError(data.error || "Failed to release cycle to managers");
      }
    } catch (err: any) {
      setError(err?.message || "Failed to release cycle to managers");
    } finally {
      setActioning(null);
    }
  };

  const handleActivate = async (id: number) => {
    setActioning(id);
    setError(null);
    try {
      const res = await fetch(`/api/cycles/${id}/activate`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...(currentUserEmail ? { "X-User-Email": currentUserEmail } : {}) },
      });
      if (res.ok) {
        await fetchCycles();
        onChanged?.();
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || "Failed to activate");
      }
    } catch (err: any) {
      setError(err?.message || "Failed to activate");
    } finally {
      setActioning(null);
    }
  };

  const handleForceDelete = async () => {
    if (!overrideModal) return;
    const trimmed = overrideConfirm.trim().toLowerCase();
    if (trimmed !== "delete") return;
    setActioning(overrideModal.id);
    setError(null);
    try {
      const res = await fetch(`/api/cycles/${overrideModal.id}/force-delete`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(currentUserEmail ? { "X-User-Email": currentUserEmail } : {}),
        },
        body: JSON.stringify({ confirm: "delete" }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setOverrideModal(null);
        setOverrideConfirm("");
        await fetchCycles();
        onChanged?.();
      } else {
        setError(data.error || "Failed to delete");
      }
    } catch (err: any) {
      setError(err?.message || "Failed to delete");
    } finally {
      setActioning(null);
    }
  };

  const startRename = (c: CycleRow) => {
    setRenamingId(c.id);
    setRenameValue(c.title);
    setError(null);
  };

  const handleViewCriteria = async (c: CycleRow) => {
    setCriteriaPreviewLoading(c.id);
    setError(null);
    try {
      const res = await fetch(`/api/cycles/${c.id}?v=${Date.now()}`, {
        cache: "no-store",
        headers: currentUserEmail ? { "X-User-Email": currentUserEmail } : {},
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(typeof data.error === "string" ? data.error : translateUi("Could not load cycle criteria."));
        return;
      }
      const preview = libraryCycleFromApiDetail(data);
      if (!preview) {
        setError(translateUi("This cycle has no criteria to preview yet."));
        return;
      }
      const frameworkId =
        typeof data.framework_cycle_id === "string" ? data.framework_cycle_id.trim() : String(data.framework_cycle_id ?? "").trim();
      const editable = Number(data.template_editable_by_company ?? 0) === 1;
      setCriteriaPreviewLocked(Boolean(frameworkId) && !editable);
      setCriteriaPreview(preview);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : translateUi("Could not load cycle criteria."));
    } finally {
      setCriteriaPreviewLoading(null);
    }
  };

  const handleViewEvents = async (c: CycleRow) => {
    setEventsPreviewLoading(c.id);
    setError(null);
    try {
      const res = await fetch(`/api/cycles/${c.id}/events?v=${Date.now()}`, {
        cache: "no-store",
        headers: currentUserEmail ? { "X-User-Email": currentUserEmail } : {},
      });
      const data = await res.json().catch(() => []);
      if (!res.ok) {
        setError(typeof data?.error === "string" ? data.error : translateUi("Could not load assessment events."));
        return;
      }
      const rows = Array.isArray(data) ? data : [];
      setEventsPreview({
        cycle: c,
        events: rows.map((row: Record<string, unknown>, index: number) => ({
          id: typeof row.id === "string" ? row.id : String(row.id ?? index),
          event_name: String(row.event_name ?? ""),
          offset_months_open: Number(row.offset_months_open ?? 0),
          offset_months_close: Number(row.offset_months_close ?? 0),
          sort_order: Number(row.sort_order ?? index),
        })),
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : translateUi("Could not load assessment events."));
    } finally {
      setEventsPreviewLoading(null);
    }
  };

  const renderEventsButton = (c: CycleRow, awaitingEvents: boolean, isDraft: boolean) => {
    if (!isDraft) return null;
    if (awaitingEvents) {
      return (
        <button
          type="button"
          onClick={() => setEventsModalCycle(c)}
          disabled={actioning === c.id}
          className={cbCn(
            "animate-pulsate inline-flex items-center justify-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-semibold",
            isDarkMode
              ? "border border-amber-400/60 bg-amber-950/50 text-amber-100 hover:bg-amber-900/60"
              : "border border-amber-400 bg-amber-50 text-amber-900 hover:bg-amber-100"
          )}
          title={translateUi("Add assessment events — required before release")}
        >
          {translateUi("Add Events")}
        </button>
      );
    }
    if (Number(c.assessment_event_count ?? 0) >= 1) {
      return (
        <button
          type="button"
          onClick={() => void handleViewEvents(c)}
          disabled={eventsPreviewLoading === c.id || actioning === c.id}
          className={cbCn(
            "inline-flex items-center justify-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-semibold",
            cb.btnSecondary(isDarkMode)
          )}
          title={translateUi("View scheduled assessment events")}
        >
          <Eye className="h-3.5 w-3.5" aria-hidden />
          <span className="hidden sm:inline">
            {eventsPreviewLoading === c.id ? translateUi("Loading…") : translateUi("View Events")}
          </span>
        </button>
      );
    }
    return null;
  };

  const editCriteriaSlotClass =
    "inline-flex shrink-0 w-9 items-center justify-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-semibold sm:w-[4.125rem]";

  const renderEditCriteriaButton = (c: CycleRow, isDraft: boolean) => {
    if (!isDraft || Number(c.template_editable_by_company ?? 0) !== 1 || !onEditCriteriaInBuilder) return null;
    return (
      <button
        type="button"
        onClick={() => onEditCriteriaInBuilder(c)}
        disabled={actioning === c.id}
        className={cbCn(editCriteriaSlotClass, cb.btnSecondary(isDarkMode))}
        title={translateUi("Edit criteria in the builder")}
      >
        <Edit2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
        <span className="hidden sm:inline">{translateUi("Edit")}</span>
      </button>
    );
  };

  const renderFixedCriteriaBadge = (c: CycleRow, isDraft: boolean) => {
    const isFixedTemplateDraft =
      isDraft &&
      onEditCriteriaInBuilder &&
      Boolean(String(c.framework_cycle_id ?? "").trim()) &&
      Number(c.template_editable_by_company ?? 0) !== 1;
    if (!isFixedTemplateDraft) return null;
    return (
      <span
        className={cbCn(
          editCriteriaSlotClass,
          "box-border cursor-default border",
          isDarkMode ? "border-amber-500/40 bg-amber-950/40 text-amber-200" : "border-amber-200 bg-amber-50 text-amber-800"
        )}
        title={translateUi("Fixed criteria — cannot be edited")}
      >
        <Lock className="h-3.5 w-3.5 shrink-0" aria-hidden />
        <span className="sr-only">{translateUi("Fixed")}</span>
      </span>
    );
  };

  const renderViewCriteriaButton = (c: CycleRow) =>
    c.company_code !== null ? (
      <button
        type="button"
        onClick={() => void handleViewCriteria(c)}
        disabled={criteriaPreviewLoading === c.id || actioning === c.id}
        className={cbCn(
          "inline-flex items-center justify-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-semibold",
          cb.btnSecondary(isDarkMode)
        )}
        title={translateUi("View criteria")}
      >
        <Eye className="h-3.5 w-3.5" aria-hidden />
        <span className="hidden sm:inline">
          {criteriaPreviewLoading === c.id ? translateUi("Loading…") : translateUi("View criteria")}
        </span>
      </button>
    ) : null;

  const renderCycleTitle = (c: CycleRow) => (
    <span className="inline-flex min-w-0 items-center gap-1">
      <span className={cbCn("font-medium", isDarkMode ? "text-slate-100" : "text-slate-800")}>{c.title}</span>
      {c.company_code !== null ? (
        <button
          type="button"
          onClick={() => startRename(c)}
          disabled={actioning === c.id}
          className={cbCn(
            "inline-flex shrink-0 rounded-md p-0.5",
            isDarkMode ? "text-slate-400 hover:bg-slate-800 hover:text-indigo-300" : "text-slate-500 hover:bg-indigo-50 hover:text-indigo-700"
          )}
          title={translateUi("Rename — use your own wording for this cycle")}
        >
          <Edit2 className="h-3.5 w-3.5" />
        </button>
      ) : null}
    </span>
  );

  const body = (
    <>
      {!embedded && (
        <>
          <h3 className={cb.heading(isDarkMode, !isMobile)}>{translateUi("Current Cycles")}</h3>
          <p className={cbCn("mt-1 text-sm", cb.body(isDarkMode))}>
            {isSystemAdmin ? translateUi("All cycles") : translateUi("Your company cycles")}
          </p>
        </>
      )}
      {error && (
        <div className={cbCn("mt-2 rounded-lg border px-3 py-2 text-sm", cb.alertError(isDarkMode))}>
          {error}
        </div>
      )}
      {loading ? (
        <div className={cbCn("mt-4 py-8 text-center text-sm", cb.bodySm(isDarkMode))}>{translateUi("Loading…")}</div>
      ) : cycles.length === 0 ? (
        <div className={cbCn("mt-4 py-8 text-center text-sm", cb.bodySm(isDarkMode))}>
          {translateUi("No cycles yet. Create one below.")}
        </div>
      ) : (
        <ul className={`mt-4 space-y-2 overflow-y-auto ${isMobile ? "max-h-[220px]" : "max-h-[280px]"}`}>
          {cycles.map((c) => (
            (() => {
              const canManageCycle = !!isSystemAdmin || c.company_code !== null;
              const canDeleteCycle = canManageCycle;
              const isDraft = c.company_code !== null && Number(c.released_to_managers ?? 1) === 0;
              const eventCount = Number(c.assessment_event_count ?? 0);
              const awaitingEvents = isDraft && eventCount < 1;
              const awaitingRelease = isDraft && eventCount >= 1;
              const status = describeCycleState(c);
              return (
            <li
              key={c.id}
              className={cbCn(
                "rounded-lg border text-sm",
                isMobile ? "px-4 py-3 min-h-[44px]" : "px-3 py-2",
                isDraft ? "grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-x-3 gap-y-1" : "flex items-center justify-between gap-2",
                c.deactivated ? cb.listRow(isDarkMode, true) : cb.templateRow(isDarkMode)
              )}
            >
              {renamingId === c.id ? (
                <>
                  <input
                    type="text"
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    className={cbCn(
                      "min-w-0 flex-1 rounded-lg border px-3 py-2 text-sm font-medium focus:outline-none focus:ring-2",
                      cb.input(isDarkMode)
                    )}
                    autoFocus
                  />
                  <div className="flex shrink-0 gap-2">
                    <button
                      type="button"
                      disabled={renameSaving}
                      onClick={() => void handleRename(c.id)}
                      className={cbCn("rounded-lg px-3 py-1.5 text-xs font-semibold", cb.btnPrimary(renameSaving))}
                    >
                      {renameSaving ? translateUi("Saving…") : translateUi("Save name")}
                    </button>
                    <button
                      type="button"
                      disabled={renameSaving}
                      onClick={() => {
                        setRenamingId(null);
                        setRenameValue("");
                      }}
                      className={cbCn("rounded-lg px-3 py-1.5 text-xs font-semibold", cb.btnSecondary(isDarkMode))}
                    >
                      {translateUi("Cancel")}
                    </button>
                  </div>
                </>
              ) : isDraft ? (
                <>
                  <div className="min-w-0 col-span-1">
                    {renderCycleTitle(c)}
                    <span className={cbCn("ml-2", cb.bodySm(isDarkMode))}>
                      ({formatCycleDurationLabel(resolveDisplayedCycleMonths(c))})
                    </span>
                    <span className={cbCn("ml-2", cb.tag(isDarkMode, "slate"))}>{translateUi("Draft")}</span>
                    <p className={cbCn("mt-0.5 text-xs", cb.bodySm(isDarkMode))}>
                      {awaitingEvents
                        ? translateUi(
                            "This draft cycle needs assessment events before it can be released to managers. Only company admins can see it until you release it."
                          )
                        : translateUi(
                            "Whilst in draft, this has not been released to managers to use and assign. Only company admins can see it until you release it."
                          )}
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-col items-center justify-center px-1 sm:px-2">
                    <span
                      className={cbCn(
                        "whitespace-nowrap text-[11px] font-semibold uppercase tracking-wide sm:text-xs",
                        awaitingEvents
                          ? isDarkMode
                            ? "text-orange-200"
                            : "text-orange-800"
                          : isDarkMode
                            ? "text-amber-200"
                            : "text-amber-800"
                      )}
                    >
                      {awaitingEvents ? translateUi("Awaiting events") : translateUi("Awaiting release")}
                    </span>
                    {awaitingEvents ? (
                      <span className={cbCn("mt-0.5 hidden text-center text-[10px] leading-tight sm:block", isDarkMode ? "text-orange-200/90" : "text-orange-800/90")}>
                        {translateUi("Required before release")}
                      </span>
                    ) : null}
                  </div>
                  <div className="flex shrink-0 items-center justify-end gap-1">
                    {renderEventsButton(c, awaitingEvents, isDraft)}
                    {renderEditCriteriaButton(c, isDraft) ?? renderFixedCriteriaBadge(c, isDraft)}
                    {renderViewCriteriaButton(c)}
                    {awaitingRelease ? (
                      <button
                        type="button"
                        onClick={() => void handleReleaseToManagers(c.id)}
                        disabled={actioning === c.id}
                        className={cbCn(
                          "inline-flex items-center justify-center whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-semibold",
                          cb.btnPrimary(actioning === c.id)
                        )}
                        title={translateUi("Release to managers — makes this cycle available for employee assignment")}
                      >
                        {actioning === c.id ? translateUi("Releasing…") : translateUi("Release")}
                      </button>
                    ) : null}
                    {canManageCycle && !isDraft && c.deactivated ? (
                      <button
                        type="button"
                        onClick={() => handleActivate(c.id)}
                        disabled={actioning === c.id}
                        className={cbCn(cb.iconBtn(isDarkMode, isMobile), cb.iconBtnHover(isDarkMode, "emerald"))}
                        title={translateUi("Reactivate – managers can assign this cycle again")}
                      >
                        <RotateCcw className="h-4 w-4" />
                      </button>
                    ) : canManageCycle && !isDraft ? (
                      <button
                        type="button"
                        onClick={() => setPendingDeactivate(c)}
                        disabled={actioning === c.id}
                        className={cbCn(cb.iconBtn(isDarkMode, isMobile), cb.iconBtnHover(isDarkMode, "amber"))}
                        title={translateUi("Suspend – no new assignments, people already on it carry on")}
                      >
                        <NoSymbol className="h-4 w-4" />
                      </button>
                    ) : null}
                    {isSystemAdmin && c.employee_count > 0 ? (
                      <button
                        type="button"
                        onClick={() => setOverrideModal({ id: c.id, title: c.title, employee_count: c.employee_count })}
                        disabled={actioning === c.id}
                        className={cbCn(cb.iconBtn(isDarkMode, isMobile), cb.iconBtnHover(isDarkMode, "red"), "text-red-600")}
                        title={translateUi("Override delete – employees still in cycle will have cycle ended by Rail Intel upon request")}
                      >
                        <ExclamationTriangle className="h-4 w-4" />
                      </button>
                    ) : null}
                    {canDeleteCycle ? (
                      <button
                        type="button"
                        onClick={() => handleDelete(c.id)}
                        disabled={!status.deletableNow || actioning === c.id}
                        className={cbCn(cb.iconBtn(isDarkMode, isMobile), cb.iconBtnHover(isDarkMode, "red"))}
                        title={status.deletableNow ? translateUi("Delete cycle") : status.unlockHint ?? undefined}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    ) : null}
                  </div>
                </>
              ) : (
                <>
              <div className="min-w-0 flex-1">
                {renderCycleTitle(c)}
                <span className={cbCn("ml-2", cb.bodySm(isDarkMode))}>
                  ({formatCycleDurationLabel(resolveDisplayedCycleMonths(c))})
                </span>
                {c.company_code === null ? (
                  <span className={cbCn("ml-2", cb.tag(isDarkMode, "indigo"))}>{translateUi("Global")}</span>
                ) : null}
                {c.deactivated ? (
                  <span className={cbCn("ml-2", cb.tag(isDarkMode, "amber"))}>{translateUi("Suspended")}</span>
                ) : null}
                {c.company_code !== null ? (
                  <span className={cbCn("ml-2", cb.tag(isDarkMode, "emeraldSoft"))}>{translateUi("Released")}</span>
                ) : null}
                <p className={cbCn("mt-0.5 text-xs", cb.bodySm(isDarkMode))}>{status.line}</p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                {renderViewCriteriaButton(c)}
                {canManageCycle && c.deactivated ? (
                  <button
                    type="button"
                    onClick={() => handleActivate(c.id)}
                    disabled={actioning === c.id}
                    className={cbCn(cb.iconBtn(isDarkMode, isMobile), cb.iconBtnHover(isDarkMode, "emerald"))}
                    title={translateUi("Reactivate – managers can assign this cycle again")}
                  >
                    <RotateCcw className="h-4 w-4" />
                  </button> ) : canManageCycle ? ( <button
                    type="button"
                    onClick={() => setPendingDeactivate(c)}
                    disabled={actioning === c.id}
                    className={cbCn(cb.iconBtn(isDarkMode, isMobile), cb.iconBtnHover(isDarkMode, "amber"))}
                    title={translateUi("Suspend – no new assignments, people already on it carry on")}
                  >
                    <NoSymbol className="h-4 w-4" />
                  </button>
                ) : null}
                {isSystemAdmin && c.employee_count > 0 ? (
                  <button
                    type="button"
                    onClick={() => setOverrideModal({ id: c.id, title: c.title, employee_count: c.employee_count })}
                    disabled={actioning === c.id}
                    className={cbCn(cb.iconBtn(isDarkMode, isMobile), cb.iconBtnHover(isDarkMode, "red"), "text-red-600")}
                    title={translateUi("Override delete – employees still in cycle will have cycle ended by Rail Intel upon request")}
                  >
                    <ExclamationTriangle className="h-4 w-4" />
                  </button>
                ) : null}
                {canDeleteCycle ? (
                  <button
                    type="button"
                    onClick={() => handleDelete(c.id)}
                    disabled={!status.deletableNow || actioning === c.id}
                    className={cbCn(cb.iconBtn(isDarkMode, isMobile), cb.iconBtnHover(isDarkMode, "red"))}
                    title={status.deletableNow ? translateUi("Delete cycle") : status.unlockHint ?? undefined}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                ) : null}
              </div>
                </>
              )}
            </li>
              );
            })()
          ))}
        </ul>
      )}
      {overrideModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setOverrideModal(null)}>
          <div
            className={cbCn(cb.modal(isDarkMode), "max-w-md w-full", isMobile ? "p-4 max-h-[90vh] overflow-y-auto" : "p-6")}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center gap-2 text-red-600">
              <ExclamationTriangle className="h-6 w-6 shrink-0" />
              <h3 className={cbCn("text-lg font-semibold", isDarkMode ? "text-slate-100" : "text-slate-900")}>
                {translateUi("Override delete cycle")}
              </h3>
            </div>
            <p className={cbCn("mb-2 text-sm", cb.body(isDarkMode))}>
              <strong>{overrideModal.employee_count}</strong> employee{overrideModal.employee_count !== 1 ? translateUi("s are") : translateUi(" is")} still assigned to <strong>{overrideModal.title}</strong> and {overrideModal.employee_count !== 1 ? "are" : "is"} within cycle duration.
            </p>
            <p className={cbCn("mb-4 text-sm", cb.body(isDarkMode))}>
              {translateUi("Deleting will remove their cycle assignment. Their employee record will show &quot;Cycle ended by Rail Intel upon request&quot;.")}
            </p>
            <label className={cbCn("mb-1 block text-sm font-medium", cb.label(isDarkMode))}>
              {translateUi("Type")} <strong>delete</strong> {translateUi("to confirm:")}
            </label>
            <input
              type="text"
              value={overrideConfirm}
              onChange={(e) => setOverrideConfirm(e.target.value)}
              placeholder="delete"
              className={cbCn(
                "w-full rounded-lg border px-3 py-2 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500",
                cb.input(isDarkMode)
              )}
              autoFocus
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setOverrideModal(null);
                  setOverrideConfirm("");
                }}
                className={cbCn("rounded-lg px-4 py-2", cb.btnGhost(isDarkMode))}
              > {translateUi("Cancel")} </button>
              <button
                type="button"
                onClick={handleForceDelete}
                disabled={overrideConfirm.trim().toLowerCase() !== "delete" || actioning === overrideModal.id}
                className="rounded-lg bg-red-600 px-4 py-2 text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {actioning === overrideModal.id ? translateUi("Deleting…") : translateUi("Delete cycle")}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      <ConfirmActionModal
        open={!!pendingDeactivate}
        dark={!!isDarkMode}
        tone="warning"
        title={translateUi("Suspend this cycle?")}
        description={
          pendingDeactivate
            ? `${translateUi(
                'Managers will no longer be able to assign "{{title}}". The {{count}} employee(s) already on it carry on to the end of their cycle.',
                { title: pendingDeactivate.title, count: pendingDeactivate.employee_count },
              )} ${describeCycleState(pendingDeactivate).unlockHint ?? translateUi("You can reactivate it at any time.")}`
            : ""
        }
        confirmLabel={translateUi("Suspend")}
        busy={actioning === pendingDeactivate?.id}
        busyLabel={translateUi("Suspending…")}
        onClose={() => {
          if (actioning == null) setPendingDeactivate(null);
        }}
        onConfirm={() => {
          if (pendingDeactivate) void handleDeactivate(pendingDeactivate.id);
        }}
      />
      <TemplateCriteriaPreviewModal
        open={!!criteriaPreview}
        cycle={criteriaPreview}
        previewKind="cycle"
        locked={criteriaPreviewLocked}
        isDarkMode={isDarkMode}
        onClose={() => {
          setCriteriaPreview(null);
          setCriteriaPreviewLocked(false);
        }}
      />
      <AssessmentEventsPreviewModal
        open={!!eventsPreview}
        cycleTitle={eventsPreview?.cycle.title ?? ""}
        expiryYears={eventsPreview?.cycle.expiry_years}
        expiryMonths={eventsPreview?.cycle.expiry_months}
        events={eventsPreview?.events ?? []}
        isDarkMode={isDarkMode}
        canEdit={Boolean(eventsPreview && Number(eventsPreview.cycle.released_to_managers ?? 1) === 0)}
        onClose={() => setEventsPreview(null)}
        onEdit={
          eventsPreview
            ? () => {
                const cycle = eventsPreview.cycle;
                setEventsPreview(null);
                setEventsModalCycle(cycle);
              }
            : undefined
        }
      />
      <AddAssessmentEventsModal
        open={!!eventsModalCycle}
        cycleId={eventsModalCycle?.id ?? null}
        cycleTitle={eventsModalCycle?.title ?? ""}
        expiryYears={eventsModalCycle?.expiry_years}
        expiryMonths={eventsModalCycle?.expiry_months}
        currentUserEmail={currentUserEmail}
        companyCode={companyCode}
        isDarkMode={isDarkMode}
        isMobile={isMobile}
        onClose={() => setEventsModalCycle(null)}
        onSaved={() => {
          void fetchCycles();
          onChanged?.();
        }}
      />
    </>
  );

  if (embedded) return <div className={isMobile ? "p-4" : "p-5 sm:p-6"}>{body}</div>;
  return <div className={cb.panel(isDarkMode, isMobile ? "sm" : "md")}>{body}</div>;
}
